<script setup lang="ts">
import { computed, onMounted, ref, watch, watchEffect } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { useSuccessStore } from '@/store/success'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { getInvoiceStatusBadge } from '@/composable/useToolsFareOptimizer'
import { IEnergyInvoiceSummary } from '@/interfaces/entities/energyInvoice'
import InvoiceHistoryDetail from './InvoiceHistoryDetail.vue'
import ManualInvoiceDialog from '@/components/FareOptimizer/Dialog/ManualInvoiceDialog.vue'
import ConfirmationModal from '@/components/Globals/ConfirmationModal.vue'
import ConsumerUnitSelector from '@/components/FareOptimizer/ConsumerUnitSelector.vue'
import CreateConsumerUnitDialog from '@/components/FareOptimizer/Dialog/CreateConsumerUnitDialog.vue'
import IcEdit from '@/components/Icons/IcEdit.vue'
import { IEnergyConsumerUnit } from '@/interfaces/entities/energyConsumerUnit'

const { t } = useI18n()
const store = useFareOptimizerStore()
const { savedInvoices, isLoadingInvoices, requiresConsumerUnitSelection, consumerUnits, selectedConsumerUnit } =
  storeToRefs(store)

const hasConsumerUnits = computed(() => consumerUnits.value.length >= 1)
const canEditSelectedUnit = computed(() => selectedConsumerUnit.value != null)

const isUnitDialogOpen = ref(false)
const unitBeingEdited = ref<IEnergyConsumerUnit | null>(null)

function openCreateUnitDialog() {
  unitBeingEdited.value = null
  isUnitDialogOpen.value = true
}

function openEditUnitDialog() {
  if (!selectedConsumerUnit.value) return
  unitBeingEdited.value = selectedConsumerUnit.value
  isUnitDialogOpen.value = true
}

function closeUnitDialog() {
  isUnitDialogOpen.value = false
  unitBeingEdited.value = null
}

const detailId = ref<number | null>(null)
const isManualDialogOpen = ref(false)

// --- Seleção de linhas para exclusão em lote ---
const selectedIds = ref<number[]>([])
const selectAllRef = ref<HTMLInputElement | null>(null)
const isDeleteDialogOpen = ref(false)
const isDeleting = ref(false)

const allSelected = computed(() => savedInvoices.value.length > 0 && selectedIds.value.length === savedInvoices.value.length)
const isIndeterminate = computed(() => selectedIds.value.length > 0 && !allSelected.value)

// Mantém o estado "indeterminado" do checkbox do cabeçalho (só via propriedade DOM).
watchEffect(() => {
  if (selectAllRef.value) selectAllRef.value.indeterminate = isIndeterminate.value
})

// Quando a lista muda (recarga, cadastro, exclusão), descarta ids que sumiram.
watch(savedInvoices, (list) => {
  const ids = new Set(list.map((invoice) => invoice.id))
  selectedIds.value = selectedIds.value.filter((id) => ids.has(id))
})

const isSelected = (id: number) => selectedIds.value.includes(id)

const toggleRow = (id: number) => {
  selectedIds.value = isSelected(id) ? selectedIds.value.filter((x) => x !== id) : [...selectedIds.value, id]
}

const toggleAll = () => {
  selectedIds.value = allSelected.value ? [] : savedInvoices.value.map((invoice) => invoice.id)
}

const clearSelection = () => {
  selectedIds.value = []
}

async function confirmDelete() {
  isDeleting.value = true
  try {
    const count = await store.deleteInvoices(selectedIds.value)
    if (count > 0) useSuccessStore().setSuccess([t('contract_optimizer.invoice_history.delete_success', { count })])
    clearSelection()
  } finally {
    isDeleting.value = false
    isDeleteDialogOpen.value = false
  }
}

onMounted(() => {
  if (!requiresConsumerUnitSelection.value) store.fetchInvoices()
})

// Colunas das métricas promovidas, espelhando a tabela "Histórico dos últimos
// 13 meses" (mesmas 6 métricas e ordem) + Consumo Total e Custo. Sempre exibidas
// (com "—" quando o mês não tiver o dado), como na tabela de origem.
type MetricKey = keyof Pick<
  IEnergyInvoiceSummary,
  | 'consumo_ponta'
  | 'consumo_fora_ponta'
  | 'consumo_total'
  | 'demanda_ponta_medida'
  | 'demanda_fora_ponta_medida'
  | 'reativo_ponta'
  | 'reativo_fora_ponta'
  | 'custo_total'
>

const visibleColumns: { key: MetricKey; label: string; unit: string; isCost?: boolean }[] = [
  { key: 'consumo_ponta', label: 'Cons. Ponta', unit: 'kWh' },
  { key: 'consumo_fora_ponta', label: 'Cons. F. Ponta', unit: 'kWh' },
  { key: 'demanda_ponta_medida', label: 'Dem. Ponta', unit: 'kW' },
  { key: 'demanda_fora_ponta_medida', label: 'Dem. F. Ponta', unit: 'kW' },
  { key: 'reativo_ponta', label: 'Reat. Ponta', unit: 'kVArh' },
  { key: 'reativo_fora_ponta', label: 'Reat. F. Ponta', unit: 'kVArh' },
  { key: 'consumo_total', label: 'Cons. Total', unit: 'kWh' },
  { key: 'custo_total', label: 'Custo', unit: 'R$', isCost: true },
]

// 'YYYY-MM-DD' -> 'MM/YYYY'
const monthLabel = (iso: string) => {
  const [year, month] = iso.split('-')
  return `${month}/${year}`
}

const formatCell = (invoice: IEnergyInvoiceSummary, col: { key: MetricKey; isCost?: boolean }) => {
  const value = invoice[col.key]
  if (value == null) return '—'
  return col.isCost ? useCurrencyFormaterFromCents(value) : value.toLocaleString('pt-BR')
}

const openDetail = (invoice: IEnergyInvoiceSummary) => {
  detailId.value = invoice.id
}
</script>

<template>
  <div class="bg-white shadow-4">
    <div v-if="hasConsumerUnits" class="border-b-2 border-stroke p-4">
      <div class="max-w-md">
        <ConsumerUnitSelector
          :creating="isUnitDialogOpen && unitBeingEdited == null"
          @create="openCreateUnitDialog">
          <template v-if="canEditSelectedUnit" #trailing>
            <button
              type="button"
              class="flex w-14 shrink-0 items-center justify-center rounded-none border border-zinc-300 bg-white text-slate-500 transition hover:border-primary hover:text-primary"
              :title="t('contract_optimizer.consumer_units.edit_aria')"
              :aria-label="t('contract_optimizer.consumer_units.edit_aria')"
              @click="openEditUnitDialog">
              <IcEdit height="16" width="16" class="!h-4 !w-4" />
            </button>
          </template>
        </ConsumerUnitSelector>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-between gap-3 border-b-2 border-stroke p-4">
      <div class="flex items-center gap-3">
        <h2 class="font-satoshi text-base font-bold text-black">{{ t('contract_optimizer.invoice_history.tab') }}</h2>
        <span v-if="selectedIds.length > 0" class="text-sm text-slate-500">
          {{ t('contract_optimizer.invoice_history.selected_count', { count: selectedIds.length }) }}
        </span>
      </div>
      <div class="flex items-center gap-2">
        <button
          v-if="selectedIds.length > 0"
          class="inline-flex items-center gap-2 rounded-none border border-red-500 px-3 py-1.5 text-sm font-medium text-red-500 transition hover:bg-red-500 hover:text-white"
          @click="isDeleteDialogOpen = true">
          {{ t('contract_optimizer.invoice_history.delete_selected') }} ({{ selectedIds.length }})
        </button>
        <button
          class="inline-flex items-center gap-2 rounded-none border border-primary px-3 py-1.5 text-sm font-medium text-primary transition hover:bg-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="requiresConsumerUnitSelection"
          @click="isManualDialogOpen = true">
          + {{ t('contract_optimizer.invoice_history.add_manual') }}
        </button>
      </div>
    </div>

    <div v-if="requiresConsumerUnitSelection" class="p-6 text-sm text-red">
      {{ t('contract_optimizer.invoice_history.empty_select_unit') }}
    </div>

    <div v-else-if="isLoadingInvoices" class="p-6 text-sm text-slate-500">...</div>

    <div v-else-if="!savedInvoices || savedInvoices.length === 0" class="p-6 text-sm text-slate-500">
      {{ t('contract_optimizer.invoice_history.empty') }}
    </div>

    <div v-else class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="border-b-2 border-stroke text-left text-xs uppercase tracking-wide text-slate-400">
            <th class="w-10 px-4 py-3">
              <input
                ref="selectAllRef"
                type="checkbox"
                class="h-4 w-4 cursor-pointer accent-primary"
                :checked="allSelected"
                :aria-label="t('contract_optimizer.invoice_history.delete_selected')"
                @change="toggleAll" />
            </th>
            <th class="whitespace-nowrap px-4 py-3">{{ t('contract_optimizer.invoice_history.month') }}</th>
            <th v-for="col in visibleColumns" :key="col.key" class="whitespace-nowrap px-4 py-3 text-right">
              {{ col.label }} <span class="normal-case text-slate-300">({{ col.unit }})</span>
            </th>
            <th class="whitespace-nowrap px-4 py-3">{{ t('contract_optimizer.invoice_history.status') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="invoice in savedInvoices"
            :key="invoice.id"
            class="cursor-pointer border-b border-stroke transition hover:bg-slate-50"
            :class="{ 'opacity-70': !invoice.complete, 'bg-blue-50': isSelected(invoice.id) }"
            @click="openDetail(invoice)">
            <td class="px-4 py-3" @click.stop>
              <input
                type="checkbox"
                class="h-4 w-4 cursor-pointer accent-primary"
                :checked="isSelected(invoice.id)"
                :aria-label="monthLabel(invoice.reference_month)"
                @change="toggleRow(invoice.id)" />
            </td>
            <td class="whitespace-nowrap px-4 py-3 font-medium text-slate-800">{{ monthLabel(invoice.reference_month) }}</td>
            <td v-for="col in visibleColumns" :key="col.key" class="whitespace-nowrap px-4 py-3 text-right">
              {{ formatCell(invoice, col) }}
            </td>
            <td class="px-4 py-3">
              <span class="inline-flex rounded px-2 py-0.5 text-xs font-semibold" :class="getInvoiceStatusBadge(invoice.complete).classes">
                {{ getInvoiceStatusBadge(invoice.complete).label }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <InvoiceHistoryDetail v-if="detailId" :invoice-id="detailId" @close="detailId = null" />
    <ManualInvoiceDialog :is-open="isManualDialogOpen" @close="isManualDialogOpen = false" />
    <CreateConsumerUnitDialog :is-open="isUnitDialogOpen" :unit="unitBeingEdited" @close="closeUnitDialog" />

    <ConfirmationModal
      :is-open="isDeleteDialogOpen"
      :cancel-text="t('contract_optimizer.invoice_history.cancel')"
      :confirmation-text="isDeleting ? t('contract_optimizer.invoice_history.delete_selected') : t('contract_optimizer.invoice_history.delete_confirm')"
      @cancel="isDeleteDialogOpen = false"
      @add="confirmDelete">
      <template #content>
        <h3 class="font-satoshi text-lg font-bold text-slate-900">{{ t('contract_optimizer.invoice_history.delete_confirm_title') }}</h3>
        <p class="mt-3 text-sm text-slate-600">
          {{ t('contract_optimizer.invoice_history.delete_confirm_message', { count: selectedIds.length }) }}
        </p>
      </template>
    </ConfirmationModal>
  </div>
</template>
